import React from "react";
import {
    Users, UserCheck, Cake, Send, ClipboardCheck, Palette, Search as SearchIcon, Sparkles,
    Clock, Grid3x3, Gem, Quote, Info, MessageSquareQuote, HelpCircle, Megaphone, PanelBottom, Plug,
    Layers, ListChecks, FolderLock, BarChart3,
    LayoutDashboard, Building2, Wallet, KeyRound, Zap, Settings, Trophy, Upload, Download,
    Gauge, ClipboardList, SlidersHorizontal,
} from "lucide-react";

// CRM workspace tabs (data-driven, API-backed).
export const CRM_TABS = [
    { id: "crm_leads", label: "Leads", icon: Users },
    { id: "crm_clients", label: "Clients", icon: UserCheck },
    { id: "crm_onboarding", label: "Onboarding", icon: ClipboardCheck },
    { id: "crm_birthdays", label: "Birthdays", icon: Cake },
    { id: "crm_reminders", label: "Reminders", icon: Send },
];

// DIY Wealth Planner admin tabs (data-driven, API-backed).
export const PLANNER_TABS = [
    { id: "planner_assets", label: "Asset Categories", icon: Layers },
    { id: "planner_questions", label: "Profiler Questions", icon: ListChecks },
    { id: "planner_plans", label: "Submitted Plans", icon: FolderLock },
    { id: "planner_analytics", label: "Analytics", icon: BarChart3 },
];

// Risk Profiler admin tabs (data-driven, API-backed).
export const RISKPROFILER_TABS = [
    { id: "rp_responses", label: "Responses", icon: ClipboardList },
    { id: "rp_questions", label: "Questions", icon: ListChecks },
    { id: "rp_settings", label: "Profile Settings", icon: SlidersHorizontal },
    { id: "rp_import", label: "Import Clients", icon: Upload },
];

// SIF Control admin tabs (data-driven, API-backed).
export const SIFCONTROL_TABS = [
    { id: "sc_overview", label: "Overview", icon: LayoutDashboard },
    { id: "sc_amcs", label: "AMCs", icon: Building2 },
    { id: "sc_sifs", label: "SIFs", icon: Wallet },
    { id: "sc_amc_users", label: "AMC Users", icon: KeyRound },
    { id: "sc_jobs", label: "Data Jobs", icon: Zap },
    { id: "sc_nav_history", label: "NAV History Fetch", icon: Download },
    { id: "sc_amc_uploads", label: "AMC Uploads", icon: Upload },
    { id: "sc_rankings", label: "Rankings", icon: Trophy },
    { id: "sc_settings", label: "Settings", icon: Settings },
];

// Website CMS tabs.
export const CMS_TABS = [
    { id: "brand", label: "Brand & Nav", icon: Palette },
    { id: "seo", label: "SEO & Sharing", icon: SearchIcon },
    { id: "hero", label: "Hero", icon: Sparkles },
    { id: "times_changing", label: "Times Changing", icon: Clock },
    { id: "benefits", label: "Benefits", icon: Grid3x3 },
    { id: "our_edge", label: "Our Edge", icon: Gem },
    { id: "philosophy", label: "Philosophy", icon: Quote },
    { id: "about", label: "About / Founders", icon: Info },
    { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
    { id: "faq", label: "FAQ", icon: HelpCircle },
    { id: "cta", label: "CTA", icon: Megaphone },
    { id: "footer", label: "Footer", icon: PanelBottom },
    { id: "integrations", label: "Integrations", icon: Plug },
];

// Legacy flat list (kept for any external reference).
export const TABS = [...CMS_TABS];

export const NAV_GROUPS = [
    { title: "CRM", tabs: CRM_TABS },
    { title: "DIY Planner", tabs: PLANNER_TABS },
    { title: "Risk Profiler", tabs: RISKPROFILER_TABS },
    { title: "SIF Control", tabs: SIFCONTROL_TABS },
    { title: "Website CMS", tabs: CMS_TABS },
];

// Tabs that are data-driven CRM pages (no CMS save/reset toolbar).
export const CRM_TAB_IDS = CRM_TABS.map((t) => t.id);
export const PLANNER_TAB_IDS = PLANNER_TABS.map((t) => t.id);
export const RISKPROFILER_TAB_IDS = RISKPROFILER_TABS.map((t) => t.id);
export const SIFCONTROL_TAB_IDS = SIFCONTROL_TABS.map((t) => t.id);

export const REC = {
    logo: "Square PNG with transparent background. ~512×512 px. Max 1 MB. Will display at ~32 px in the navbar.",
    hero: "Tall portrait. 1200×1500 px (4:5 ratio). JPG/WebP. Max 3 MB.",
    family: "Landscape. 1400×1120 px (5:4 ratio). JPG/WebP. Max 3 MB.",
    benefit: "Landscape. 1200×900 px (4:3 ratio). JPG/WebP. Max 3 MB.",
    edge: "Landscape. 1600×1200 px (4:3 ratio). JPG/WebP. Max 3 MB.",
    philosophy: "Portrait. 1000×1250 px (4:5 ratio). JPG/WebP. Max 3 MB.",
    founder: "Portrait. 1000×1250 px (4:5 ratio). Desaturated/warm tones work best. JPG/WebP. Max 3 MB.",
    testimonial: "Portrait. 1000×1250 px (4:5 ratio). JPG/WebP. Max 3 MB.",
    og: "Landscape. 1200×630 px (1.91:1 ratio). PNG or JPG. This image appears when the site is shared on social media.",
    favicon: "Square. 64×64 or 128×128 px PNG/ICO. Appears in the browser tab.",
};

export const Field = ({ label, value, onChange, type = "text", testId, hint }) => (
    <label className="block mb-5">
        <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">{label}</div>
        {type === "textarea" ? (
            <textarea
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-3 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors min-h-[100px]"
                data-testid={testId}
            />
        ) : (
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-white border border-oth-line/70 rounded-2xl px-4 py-3 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors"
                data-testid={testId}
            />
        )}
        {hint && <div className="text-[11px] text-oth-ink3 mt-1.5 font-body">{hint}</div>}
    </label>
);

export const ArrayEditor = ({ items, onChange, render, addNew, label, addLabel = "Add item", testId }) => (
    <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">{label}</div>
        <div className="space-y-4" data-testid={testId}>
            {(items || []).map((it, i) => (
                // Editable list — index key is intentional so deleting the last
                // row doesn't accidentally retain the previous row's input focus.
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="rounded-2xl border border-oth-line/70 bg-white p-5 relative">
                    <button
                        type="button"
                        onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                        className="absolute top-3 right-4 text-xs text-oth-ink3 hover:text-red-600 font-body"
                        data-testid={`${testId}-remove-${i}`}
                    >Remove</button>
                    {render(it, (next) => onChange(items.map((x, idx) => (idx === i ? next : x))), i)}
                </div>
            ))}
        </div>
        <button
            type="button"
            onClick={() => onChange([...(items || []), addNew()])}
            className="mt-4 btn-pill btn-pill-blue text-sm"
            data-testid={`${testId}-add`}
        >+ {addLabel}</button>
    </div>
);
